import dayjs from "dayjs";
import React from "react";
import { getPercentages as percentages } from "../Colors/percentages";
import { captureElement } from "./capture_utils";
import jsPDF from "jspdf";
import { DATE_FORMAT } from "./date_utils";
import { v1 as uuidv1 } from "uuid";
import { audioMessage as message } from "../Components/Common/Notifications/audioMessage";
import {
	getRestTravelBarColor,
} from "../Components/Team/TeamAvgCharts";

export const extractGameDates = (trainings) => {
  if (!trainings?.data) return {};
  
  return trainings.data.reduce(
    (cur, data) => ({ ...cur, [data.date]: data.opponent }),
    {}
  );
};

export const filterTrainingData = (trainings, showGameDays, gameDates) => {
  if (!showGameDays || !trainings?.data) {
    return trainings;
  }


  const filteredData = trainings.data.filter((data) => {
    if (data.game_id) gameDates[data.date] = true;
    return data.game_id;
  });

  const filteredWellness = Object.keys(trainings?.wellness || {}).reduce(
    (cur, key) => ({
      ...cur,
      [key]: trainings?.wellness?.[key]?.filter(
        (data) => gameDates[data.date]
      ),
    }),
    {}
  );

  return {
    ...trainings,
    data: filteredData,
    wellness: filteredWellness,
  };
};

export const get_metric_info_from_identifier = (m_id) => {
	let arr = m_id?.split('-') || ["", "", ""];
	return {
	  metric_id: arr[0] + "-" + arr[1],
	  metric_stat: arr[2]
	}
}

export const isAssignedMetric = (metric_identifier_with_stat = '', metrics = []) => {
	const teamMetrics = metrics.map( (metric) => metric.unique_identifier)
	const {metric_id} = get_metric_info_from_identifier(metric_identifier_with_stat);

	if (!metric_id || metric_id === "-") {
		return false
	}

	if (metric_id.startsWith("gps")) {
		// Active UDMs appear in the team metrics list with a gps-c{id} unique_identifier,
		// so the membership check covers them; deleted/inactive UDMs correctly fall out.
		return teamMetrics.includes(metric_id)
	}

	// if the metric is a testing/forceplate/wellness metric, return true
	return true
}

export const filterChartDataByDateRange = (data, startDate, endDate) => {

	if (!data) return [];
  if(!startDate || !endDate) return data
	
  const filteredData = data.filter(
    (dt) =>
      dayjs(startDate, "YYYY-MM-DD") <= dayjs(dt.date, "YYYY-MM-DD") &&
      dayjs(dt.date, "YYYY-MM-DD") <= dayjs(endDate, "YYYY-MM-DD")
  );

	
  const sortedData = filteredData.sort((a, b) => 
    dayjs(a.date, "YYYY-MM-DD").valueOf() - dayjs(b.date, "YYYY-MM-DD").valueOf()
  );

  return sortedData;
};

export const getRestAndTravelDaysInChart = (
	startDate,
	endDate,
	data,
	restAndTravel,
	showRest,
	showTravel
  ) => {
	if (!data) return [];
	const datesWithData = data.reduce((acc, item) => {
	  acc[item.date] = true;
	  return acc;
	}, {});
	let result = [];
	for (
	  let date = dayjs(startDate);
	  date <= dayjs(endDate);
	  date = date.add(1, "day")
	) {
	  const dateString = date.format(DATE_FORMAT);
	  if (
		(!datesWithData || !datesWithData[dateString]) &&
		restAndTravel &&
		restAndTravel[dateString]
	  ) {
		const parsed = {
		  date: dateString,
		  rest: restAndTravel[dateString] >= 2,
		  travel: restAndTravel[dateString] % 2 === 1,
		};
		if ((showRest && parsed.rest) || (showTravel && parsed.travel)) {
		  result.push({
			...parsed,
			rest: parsed.rest && showRest,
			travel: parsed.travel && showTravel,
		  });
		}
	  }
	}
	return result;
};

export function gpsChartxAxisLabels(data, isTeamChart){
	if(isTeamChart) {
		return data.map((item) => item.name);
	}
	return data.map((item) => item.opponent || dayjs(item.date).format('MM-DD'));
}

export function getBarHexColors(training, field, wellness, colors_100, wellness_info = {}) {

	if (training.rest || training.travel) {
			return getRestTravelBarColor(training);
	}
	if (training.rest || training.travel) {
			return "#808080";
	}
	let top_color = "#d9534f";
	let bottom_color = "#31B452";
	if (wellness === true) {
			if (wellness_info["no_color"] === true) {
					return "#808080";
			}
			top_color = "#31B452";
			bottom_color = "#d9534f";

			if (wellness_info["low_good"] === true) {
					top_color = "#d9534f";
					bottom_color = "#31B452";
			}
			if (training[field] >= wellness_info["upper"]) {
					return top_color;
			} else if (training[field] >= wellness_info["lower"]) {
					return "#FFC83D";
			} else {
					return bottom_color;
			}
	}
	if (training[field] > colors_100[field] * percentages(field).upper) {
			return top_color;
	} else if (
			colors_100[field] * percentages(field).middle <= training[field] &&
			training[field] <= colors_100[field] * percentages(field).upper
	) {
			return "#EF7616";
	} else if (
			colors_100[field] * percentages(field).lower <= training[field] &&
			training[field] <= colors_100[field] * percentages(field).middle
	) {
			return "#FFC83D";
	} else if (
			colors_100[field] * percentages(field).lower > training[field] &&
			training[field] != null
	) {
			return bottom_color;
	} else {
			return "#000000";
	}
}

export function round(num) {
	return Math.round((num + Number.EPSILON) * 10) / 10;
}

export function round2(num) {
	return Math.round((num + Number.EPSILON) * 100) / 100;
}

export const custom_label_for_game_days = ({ disp_date = "", opponent }) => {
	return opponent ? opponent : disp_date;
};

export const MAX_VELO_METRICS = [
	"max_velo_50_69_perc",
	"max_velo_70_79_perc",
	"max_velo_80_85_perc",
	"max_velo_gt_85_perc",
	"max_velo_50_69_effs",
	"max_velo_70_79_effs",
	"max_velo_80_85_effs",
	"max_velo_gt_85_effs",
];

export const TOTAL_DISTANCE_METRICS = [
	"total_distance_75_85_perc",
	"total_distance_85_90_perc",
	"total_distance_90_95_perc",
	"total_distance_gt_85_perc",
	"total_distance_gt_95_perc",
]

export const getMetricsToRender = (team_name, metrics = [], custom_chart_types = []) => {
    if(team_name === "Purdue Football") {
      metrics = prependMaxVeloInMetrics(metrics) || [];
    }

	const metricsByKey = Object.fromEntries(metrics.map((m) => [m.key, m]));
	const allChartsToRender = custom_chart_types
		.map((chart) => {
			const key = chart?.bar_metric || chart?.bar1_metric || chart?.stack_type;
			const metric = key ? metricsByKey[key] : null;
			return metric ? { ...metric, customChartData: chart } : null;
		})
		.filter(Boolean);
	return allChartsToRender.filter((e) => e.customChartData || (e.visible === true || e.visible === undefined));
  }

export function CustomTooltip({
	payload,
	label,
	active,
	is_distance,
	showACWR,
}) {
	if (payload == null) return (<></>);

	let total_distance =
		is_distance == "distance" ? payload[0]?.payload?.distance : undefined;

	if (
		payload?.length > 0 &&
		(payload[0].payload.rest || payload[0].payload.travel)
	) {
		const pd = payload[0].payload;
		return (
			<div className="custom-tooltip">
				<p
					className="custom-tooltip-item"
					style={{ color: getRestTravelBarColor(pd) }}
				>
					{pd.rest && pd.travel
						? "Rest + Travel Day"
						: pd.rest
						? "Rest Day"
						: "Travel Day"}
				</p>
			</div>
		);
	}

	const dist_tooltip = (payload) => (
		<>
			{payload
				?.filter((el) => el?.name?.startsWith("Distance"))
				.map((item) => (
					<p
						className="custom-tooltip-item"
						style={{ color: `${item.fill || "#ccc"}` }}
					>
						{`${item.name}: ${item.value}`}
					</p>
				))}
			<p className="custom-tooltip-item">{`Total Distance: ${
				total_distance ||
				round(
					payload[0].value +
						payload[1].value +
						payload[2].value +
						payload[3].value +
						payload[4].value
				)
			}`}</p>
			<p className="custom-tooltip-item" style={{ color: "#47aac5" }}>{`${
				showACWR ? "Per Minute" : "ACWR"
			}: ${payload?.find((x) => x.name == "ACWR")?.value || 0}`}</p>
		</>
	);

	const non_dist_tooltip = (payloads) => (
		<>
			{payloads.map((payload, i) => (
				<p
					key={i}
					className="custom-tooltip-item"
					style={{
						color: `${
							payload.name !== "ACWR" ? payload.fill : "#47aac5"
						}`,
					}}
				>{`${
					showACWR && payload.name === "ACWR"
						? "Per Minute"
						: payload.name
				}: ${payload.value}`}</p>
			))}
		</>
	);
	if (active) {
		return (
			<div className="custom-tooltip">
				{payload[0]?.payload.opponent ? (
					<p className="custom-tooltip-item">{`${payload[0]?.payload.opponent}`}</p>
				) : null}
				{payload[0]?.payload.disp_date ? (
					<p className="custom-tooltip-item">{`${payload[0]?.payload.disp_date}`}</p>
				) : null}
				{payload[0]?.dataKey.startsWith("distance") &&
				payload[0]?.name == "Distance 1"
					? dist_tooltip(payload)
					: non_dist_tooltip(payload)}
			</div>
		);
	}
	return null;
}

export function findNearestNumber(answers, value) {
	let nearestAnswer = null;
	let nearestDifference = Infinity;
	let i = 0,
		index = -1;

	for (const answer of answers) {
		const difference = Math.abs(answer.value - value);
		if (difference === 0) {
			return [answer, i];
		} else if (difference < nearestDifference) {
			nearestAnswer = answer;
			nearestDifference = difference;
			index = i;
		}
		i++;
	}

	return [nearestAnswer, index];
}

export const getAnswerByValue = (metrics, key, value) => {
	const metric = metrics.find((m) => m.key === key);
	if (metric && metric.answers) {
		const [answer, index] = findNearestNumber(metric.answers, value);
		if (answer) {
			return [answer.answer, index];
		}
	}
	return [null];
};

export const getAnswerAndIndex = (value, metric, metrics) => {
	return ["float", "int"].includes(metric.answer_type?.toLowerCase())
		? [value || 0, -1]
		: getAnswerByValue(metrics, metric.key, value);
};

export const formatLabelForChart = (value) => {
	const limit = 15; // put your maximum character
	if (!isNaN(value) || value?.length < limit) return value;
	return `${value?.substring(0, limit)}...`;
};

export function GetDataForChartConfig(config, data = [], got_drill_data) {
	let resp = [];

	let filtered = filterDataScope(config.scope, data);

	if (got_drill_data) {
		filtered = filterDataDrill(config.drills || [], filtered);
	}

	let grouped = groupBy(filtered, "date");

	Object.keys(grouped).forEach((each_date) => {
		let newObj = {};
		let pfx = config.scope.join() + ";";

		newObj[pfx + config.metric] = apply_agg(
			grouped[each_date],
			config.statistic,
			config.metric
		);
		newObj[pfx + config.metric + "_ACWR"] = apply_agg(
			grouped[each_date],
			config.statistic,
			config.metric + "_ACWR"
		);

		newObj[config.metric] = apply_agg(
			grouped[each_date],
			config.statistic,
			config.metric
		);
		newObj[config.metric + "_ACWR"] = apply_agg(
			grouped[each_date],
			config.statistic,
			config.metric + "_ACWR"
		);

		newObj["date"] = each_date;
		newObj["disp_date"] = each_date.substring(5);
		resp.push(newObj);
	});

	resp = resp.sort((a, b) => {
		return a.date - b.date;
	});
	return resp;
}

const sum = (arr) => arr.reduce((a, b) => a + b, 0);
const avg = (arr) => sum(arr) / arr.length;

const apply_agg = (data, agg, metric) => {
	if (agg == "Average") return round(avg(data.map((x) => x[metric])));
	if (agg == "Minimum") return round(Math.min(...data.map((x) => x[metric])));
	if (agg == "Maximum") return round(Math.max(...data.map((x) => x[metric])));
};

const filterDataDrill = (drills, data) => {
	let result = [];

	drills.map((drill_id) => {
		result = result.concat(
			data.filter((datapoint) => drill_id == datapoint.drill_id_id)
		);
	});

	return result;
};

const filterDataScope = (scopes, data) => {
	let unique_datapoints = new Set();

	if (scopes.includes("Entire Team")) {
		return data;
	}

	scopes.map((scope) => {
		if (scope.startsWith("Position: ")) {
			let pos = scope.substring(scope.indexOf(":") + 1).trim();
			data.filter((datapoint) => datapoint.position == pos).map((x) =>
				unique_datapoints.add(x.player_id_id + "#" + x.date)
			);
		}

		if (scope.startsWith("Player: ")) {
			let player = scope
				?.substring(scope?.indexOf(":") + 1, scope?.indexOf("#"))
				?.replace(",", "")
				?.trim();
			data?.filter(
				(datapoint) =>
					player == datapoint?.first_name + " " + datapoint?.last_name
			).map((x) => unique_datapoints?.add(x?.player_id_id + "#" + x?.date));
		}
	});

	return data.filter((x) =>
		unique_datapoints.has(x.player_id_id + "#" + x.date)
	);
};

const groupBy = (array, key) => {
	// Return the end result
	return array.reduce((result, currentValue) => {
		// If an array already present for key, push it to the array. Else create an array and push the object
		(result[currentValue[key]] = result[currentValue[key]] || []).push(
			currentValue
		);
		// Return the current iteration `result` value, this will be taken as next iteration `result` value and accumulate
		return result;
	}, {}); // empty object is the initial value for result object
};

export const getTooltipMetricName = (config) => {
	if (config.scope.includes("Entire Team")) return "Entire Team";

	let pos = [];
	let players = [];

	config.scope.map((x) => {
		if (x.startsWith("Player: ")) {
			players.push(x.slice(x.indexOf("#")).trim());
		} else if (x.startsWith("Position: ")) {
			pos.push(x.slice(x.indexOf(":") + 1).trim());
		}
	});

	let res = "";

	if (players.length > 0) {
		res += "Players: [" + players.toString() + "]";
	}
	if (pos.length > 0) {
		res += " Positions: [" + pos.toString() + "]";
	}

	return res;
};

export const vbtSuffixes = [
	"(Average Velocity)",
	"(Average Force)",
	"(Bar Weight)",
	"(Peak Velocity)",
	"(Average Power)",
	"(Peak Power)",
	"(Velocity)",
	"(Peak Velocity)",
	"(Total Time)",
	"(Top Speed)",
	"(Eccentric Velocity)",
	"(Peak Eccentric Velocity)",
	"(Peak Eccentric Power)",
	"(Peak RFD)",
	"(Conc. Average Force)",
	"(Conc. Average Power)",
	"(Conc. Average Velocity)",
	"(Conc. Peak Force)",
	"(Conc. Peak Power)",
	"(Conc. Peak Velocity)",
	"(Ecc. Average Velocity)",
	"(Ecc. Peak Force)",
	"(Ecc. Peak Power)",
	"(Ecc. Peak Velocity)",
	"(Mean Watts/lb)",
	"(Peak Watts/lb)",
	"(RFD)",
	"(RSI)",
	"(Avg Force)",
	"(Max Force)",
	"(Impulse)",
	"(% 1RM)",
	"(Time to Peak Power)",
	"(Time to Peak Velocity)",
	"(Displacement)",
	"(Eccentric Time)",
	"(Total Work)",
	"(Velocity at 100ms)",
	"(Average Eccentric Velocity)",
	"(Average Accel)",
	"(Peak Accel)",
	"(Peak Force)",
	"(MPV)",
	"(ROM)",
	"(Torque)",
	"1RM",
	"(Max Force Avg)",
	"(Torque Ratio)"
];

export const asymPrefixes = [
	"Left",
	"Right",
	"Asym",
	"Bilateral"
]

export const fillMissingDatesForChart = (data, startDate, endDate) => {
  if (!data || !startDate || !endDate) return data || [];
  const existingDates = new Set(data.map((d) => d.date));
  const nullTemplate = data.length > 0
    ? Object.keys(data[0]).reduce((acc, k) => {
        if (k !== 'date' && k !== 'disp_date') acc[k] = null;
        return acc;
      }, {})
    : {};
  const filled = [...data];
  for (
    let date = dayjs(startDate, DATE_FORMAT);
    date <= dayjs(endDate, DATE_FORMAT);
    date = date.clone().add(1, "day")
  ) {
    const dateString = date.format(DATE_FORMAT);
    if (!existingDates.has(dateString)) {
      filled.push({ ...nullTemplate, date: dateString, disp_date: date.format("MM-DD") });
    }
  }
  return filled.sort((a, b) => (a.date < b.date ? -1 : 1));
};

const withDummyDates = (data, all_dates, metric_key, prefix) => {
	return all_dates.map((curr_date) => {
		let dp = data.find((x) => x.date == curr_date);
		if (dp !== undefined) {
			return dp;
		} else {
			let obj = {};
			obj["disp_date"] = curr_date.substring(5);
			obj[prefix.concat(metric_key)] = 0;
			return obj;
		}
	});
};

export const getPrediction = (date, practicePlan, costPerMin, key) => {
	const filteredPrediction = practicePlan.filter(
		(plan) => plan.date === date
	);
	if (filteredPrediction.length == 0) return 0;
	let prediction = ["top_speed", "accel_max", "decel_max", "top_speed_percent"].includes(key)
		? Math.max(
				...costPerMin
					.filter((c) =>
						practicePlan.map((d) => d.drill_id).includes(c.drill_id)
					)
					.map((o) => o?.[key] || 0)
		  )
		: filteredPrediction.reduce(
				(cur, plan) =>
					cur +
					(["distance_per_minute", "max_hr", "mean_hr"].includes(
						key
					) || key.includes("per_min")
						? 1
						: plan.duration) *
						(costPerMin.find(
							(cost) => cost.drill_id === plan.drill_id
						) || {})[key],
				0
		  ) /
		  (["distance_per_minute", "max_hr", "mean_hr"].includes(key) ||
		  key.includes("per_min")
				? filteredPrediction.length || 1
				: 1);
	// hacky fix for top speed pred = -inf in some cases.
	if (prediction == Number.NEGATIVE_INFINITY) {
		prediction = 0;
	}
	return round(prediction || 0);
};
export function generateComboChartLegendPayload({ items, metricOptions }) {

	let res = [];

	let i = 0;

	items?.map((item) => {
		// else {
		//   color = chart_colors[i]
		//   i+=1
		//   if (i > chart_colors.length-1){
		//     i=0;
		//   }
		// }

		let metric_key = item.config.metric;
		let metric_name = (metricOptions || []).find(
			(x) => x.key == metric_key
		)?.name;
		let name_prefix = getTooltipMetricName(item.config);
		//let drill_names = config.drills.map(d_id => drillList?.find(x=>x.drill_id == d_id).name);
		let drill_names = [];
		let barname =
			name_prefix +
			": " +
			metric_name +
			" " +
			drill_names +
			" (" +
			item.config.statistic +
			")";

		//line' | 'plainline' | 'square' | 'rect'| 'circle' | 'cross' | 'diamond' | 'square' | 'star' | 'triangle' | 'wye
		res.push({
			value: barname,
			color: item.config.use_custom_color
				? item.config.custom_color
				: item.config.chartType == "Line"
				? DEFAULT_LINE_COLOR
				: "#000",
			type: item.config.chartType == "Bar" ? "circle" : "line",
			id: uuidv1(),
		});
	});

	return res;
}

const DEFAULT_LINE_COLOR = "#46AAC5";

const default_color_palette = {
	top_speed: 26.11154,
	trimp: null,
	trimp_per_minute: null,
	max_hr: null,
	mean_hr: null,
	distance_per_minute: 90,
	distance: 12.284615384615385,
	high_speed_running: 791.3461538461538,
	accel: 20.615384615384617,
	decel: 30.23076923076923,
	sprints: 5.230769230769231,
	RPE: 10,
	session_RPE: 1500,
	fatigue: 5,
	mood: 5,
	muscle_soreness: 5,
	sleep_quality: 5,
	stress_level: 5,
	hrs_sleep: 12,
	days_since_85: 10,
	days_since_x_top_speed: 10,
};

export function get_acwr_limits(team_name) {
	let res = {
		upper: 1.3,
		lower: 0.8,
	};
	if (team_name == "Georgia Tech Football") {
		res.upper = 1.5;
	}
	return res;
}

export const getValueColor = (
	colors,
	{ value, textAnswerIndex },
	metricKey,
	wellness = undefined
) => {
	if (wellness) {
		if (wellness["no_color"] === true) {
			return "#808080";
		}

		let [bottomColor, middleColor, topColor] = [
			"#d9534f",
			"#FFC83D",
			"#31B452",
		];

		if (wellness["low_good"])
			[bottomColor, topColor] = [topColor, bottomColor];

		const modifiedValue =
			textAnswerIndex === -1
				? value
				: wellness.answers[textAnswerIndex].value;

		if (modifiedValue >= wellness["upper"]) {
			return topColor;
		} else if (
			modifiedValue >= wellness?.["middle"] ||
			modifiedValue >= wellness?.["lower"]
		) {
			return middleColor;
		} else {
			return bottomColor;
		}
	} else {
		let colorArray = ["#31B452", "#FFC83D", "EF7616", "#d9534f"];
		const color = colors[metricKey];
		const { upper, middle, lower } = percentages(metricKey);
		if (value >= color * upper) {
			return colorArray[3];
		} else if (value >= color * middle) {
			return colorArray[2];
		} else if (value >= color * lower) {
			return colorArray[1];
		} else if (value != null) {
			return colorArray[0];
		} else {
			return "#000000";
		}
	}
};

export function prependMaxVeloInMetrics(all_data) {
	let sorted_data = [];

	for (let i = 0; i < MAX_VELO_METRICS.length; i++) {
		let e = all_data.find((x) => x.key == MAX_VELO_METRICS[i]);
		if (e) {
			sorted_data.push(e);
		}
	}

	for (let i = 0; i < all_data.length; i++) {
		if (!MAX_VELO_METRICS.includes(all_data[i].key)) {
			sorted_data.push(all_data[i]);
		}
	}
	return sorted_data;
}

export function purdueFootballMetricOrder(
	fixed_cols,
	metric_columns,
	include_top_speed_perc = false
) {
	let order = [
		...fixed_cols,
		"player_load",
		"distance",
		"hard_runs",
		"high_speed_running",
		"sprints",
		"sprint_distance",
		"max_velo_50_69_effs",
		"max_velo_50_69_perc",
		"max_velo_70_79_effs",
		"max_velo_70_79_perc",
		"max_velo_80_85_effs",
		"max_velo_80_85_perc",
		"max_velo_gt_85_effs",
		"max_velo_gt_85_perc",
		"top_speed",
		"impacts",
	];

	if (
		include_top_speed_perc &&
		order.includes("top_speed") &&
		metric_columns.find((col) => col.key == "top_speed_percent")
	) {
		order.splice(order.indexOf("top_speed") + 1, 0, "top_speed_percent");
	}

	return order
		.map((x) =>
			metric_columns.find((col) => col.key == x || col.dataIndex == x)
		)
		.filter((a) => !!a);
}

export const isVBTMetric = (value) => {
	if (value?.includes("13m Sprint") || value?.includes("19m Sprint") || value?.includes("34m Sprint") || value?.includes("Bilateral Squat")) {return false}
	return vbtSuffixes?.map((suf) => value?.includes(suf))?.includes(true);
};

export const isNordicMetric = (value) => {
	return (
		value &&
		(value.includes("Nordic") ||
			value.includes("ISO") ||
			value.includes("NB Custom")) &&
		!(
			value.includes("SL Knee ISO") ||
			value.includes("SL Ankle ISO") ||
			value.includes("SL Hip ISO")
		)
	);
};

let asym_forceplate_metrics = {}
let non_asym_forceplate_exercises = {}

export const setAsymForceplateMetrics = (asym_metrics) => {
	if (!Object.keys(asym_forceplate_metrics).length) {
		asym_forceplate_metrics = asym_metrics
	}
}

export const setNonAsymExercises = (non_asym_exercises) => {
	if (!Object.keys(non_asym_forceplate_exercises).length) {
		non_asym_forceplate_exercises = non_asym_exercises
	}
}

export const isForcePlateAsymMetric = (metric_name, with_main_key = false) => {
	if (with_main_key) {
		for (const [key, values] of Object.entries(asym_forceplate_metrics)) {
			if (values.includes(metric_name)) {
				return key;
			}
		}
		return false;
	}
	return Object.values(asym_forceplate_metrics).some((array) =>
		array.includes(metric_name)
	);
};

export const mergeNordicMetricsForDropdown = (dropdown) => {
	if (!dropdown) return dropdown;
	if (typeof dropdown !== "object") return dropdown;
	Object.keys(dropdown).forEach((key) => {
		dropdown[key].sort((a, b) => (a.key < b.key ? -1 : 1));
	});
	const merged = {};
	Object.keys(dropdown).forEach((key) => {
		if (!isNordicMetric(key)) {
			merged[key] = dropdown[key];
			return;
		}
		const mergedKey = key?.replace(" Left", "")?.replace(" Right", "");
		if (!merged[mergedKey]) merged[mergedKey] = dropdown[key];
		else {
			if (merged[mergedKey] && merged[mergedKey].length > 0) {
				merged[mergedKey] = merged[mergedKey].map((x) => ({
					...x,
					key: `${x.key?.replace("Right", "Left")}`,
				}));
			} else {
				delete merged[mergedKey];
				merged[
					`${key?.replace("Right", "Left")}:${key?.replace(
						"Left",
						"Right"
					)}`
				] = [];
			}
		}
	});
	return merged;
};

export const getStatLabelFromKey = (
	key,
	metricType = "testing",
	keyStats,
	teamName
) => {
	if (metricType === "forceplate" && key?.includes(":")) {
		return isForcePlateAsymMetric(
			getTestingMetricFromKey(
				metricType,
				key.split(":")[0],
				keyStats,
				teamName
			)?.metric,
			true
		);
	}
	return isNordicMetric(key)
		? key.split(":")[0]?.replace(" Left", "")?.replace(" Right", "")
		: getTestingMetricFromKey(metricType, key, keyStats, teamName)?.metric;
};

export const generateVBTMetricTree = (metrics, keyType = "name") => {
	let items = {};

	metrics
		.map((metric) => {
			const [app, metricName] = getAppName(metric.name);
			const isVBT = metric?.metadata?.tags?.includes("vbt");
			const isNordbord =
				metric?.metadata?.tags?.includes("nordbord") &&
				metric?.metadata?.is_asym == true;
			const endsWithVbtSuffix = vbtSuffixes.some((s) => metricName.endsWith(s))
			const startsWithAsymPrefix = asymPrefixes.some((s) => metricName.startsWith(s))
			const isTenEightyMotionTestingMetric = (metric?.metadata?.tags?.includes("forceframe") == true && endsWithVbtSuffix) && (startsWithAsymPrefix || metricName.includes("Sprint")) || metricName.includes("13m Sprint") || metricName.includes("19m Sprint") || metricName.includes("34m Sprint") || metricName.includes("Bilateral Squat")
			

			if (!isVBT && !isNordbord && !isTenEightyMotionTestingMetric) {
				return [metricName];
			}

			for (let i = 0; i < vbtSuffixes.length; i++) {
				if (metricName.includes(vbtSuffixes[i])) {
					const idx = metricName.indexOf(vbtSuffixes[i]);
					const exerciseName = metricName.substring(0, idx).trim();
					const key =
						keyType === "name"
							? exerciseName
							: `${metric.metric_type}-${metric.metric_id}`;
					let rest = metricName.substring(idx);
					if (rest != "1RM") {
						rest = rest.slice(1, -1);
					}
					return [key, rest];
				}
			}
			return [metricName];
		})
		.forEach((element) => {
			let exercise = element[0];
			if (items[exercise] == undefined) {
				items[exercise] = [];
			}
			if (element.length > 1) {
				let metric = element[1];
				let suffix = metric == "1RM" ? "1RM" : `(${metric})`;
				items[exercise].push({
					key: `${exercise} ${suffix}`,
					label: metric,
				});
			}
		});
	
	return items;
};

export const format_label = (str) => {
	try {
		if (str.includes("(Speed Sig)")) {
			let idx = str.indexOf("(Speed Sig)");
			return (
				<big>
					{str.slice(0, idx)} <br /> {str.slice(idx)}
				</big>
			);
		}

		if (/.*\([a-zA-Z0-9\-].*\)/.test(str)) {
			const idx1 = str.indexOf("(");
			if (idx1 > 0) {
				str = (
					<big>
						{str.slice(0, idx1)} {str.slice(idx1)}{" "}
					</big>
				);
			}
			return str;
		}

		const idx = str.indexOf("(");
		if (idx > 0) {
			str = (
				<>
					{" "}
					{str.slice(0, idx)} <br /> {str.slice(idx)}{" "}
				</>
			);
		}
		return <big>{str}</big>;
	} catch (err) {
		return <big>{str}</big>;
	}
};

export const getTestingMetricFromKey = (
	metricType,
	metric,
	keyStats,
	teamName
) => {
	return keyStats.find((x) => x.metric == metric);
};

export const getStatisticLatestDateString = (metricStat) => {
	return metricStat && metricStat.latest_date
		? `Latest date: ${dayjs(metricStat.latest_date).format("MMM D, YYYY")}`
		: "";
};

export const getRestTravelBarColorForHighcharts = (training) => {
	if (training?.rest && training?.travel) return "#722ed1";
	if (training?.rest) return "#ADD8E6";
	if (training?.travel) return "#f56a00";
	return "#808080";
}

export const addPredictionSeriesConfig = (
	options, metricName, metricKey, primaryPredSeries, otherSeriesData, series, dataColorUtil = null, playerId = null
) => {
	const primaryPredSeriesConfig = {
	  name: `${metricName} Prediction`,
	  type: 'column',
	  yAxis: 0,
	  data: primaryPredSeries.map((y, i) => ({
		y,
		color: getBarColorsForHighcharts({[metricKey]: y}, metricKey, false, null, dataColorUtil, playerId),
		dataLabels: {
		  enabled: !series[i]?.y && y ? true : false
		}
	  })),
	  opacity: 0.6,
	  zIndex: 0,
	  showInLegend: false
	}
  
	options.series.push(primaryPredSeriesConfig)
	options.plotOptions.column = {
	  ...options.plotOptions.column,
	  shadow: false,
	  grouping: false
	}
  
	// Adjust main column series to achieve the desired overlap affect
	options.series[0] = { 
	  ...options.series[0],
	  pointPadding: 0.3,
	  pointPlacement: 0,
	}

	for (let i = 0; i < otherSeriesData.length; i++) {
		const otherMetricName = otherSeriesData[i].name;
		const otherPlotType = otherSeriesData[i].plotType;
		const seriesData = otherSeriesData[i].data;
		const otherSeriesColor = otherSeriesData[i].color;
		const otherSeriesIdx = otherSeriesData[i].index;
		const yAxis = otherSeriesData[i].yAxis;
		const extraOptions = otherSeriesData[i].extraOptions;
		const secondaryPredSeriesConfig = {
			name: `${otherMetricName} Prediction`,
			type: otherPlotType,
			yAxis: yAxis,
			data: seriesData,
			color: otherSeriesColor ? otherSeriesColor() : '#a6a6a6',
			...extraOptions,
			opacity: 0.6,
			zIndex: -1,
			showInLegend: false
		}
	
		options.series.push(secondaryPredSeriesConfig)
		options.plotOptions.column = {
			...options.plotOptions.column,
			shadow: false,
			grouping: false
		}
	
		// Adjust main column series to achieve the desired overlap affect
		options.series[otherSeriesIdx] = { 
			...options.series[otherSeriesIdx],
			pointPadding: 0.3,
			pointPlacement: 0,
		}
	}
  
	return options;
  }

export const getBarColorsForHighcharts = (training, field, wellness, wellness_info = {}, dataColorUtil=undefined, playerId=null) => {
	if (training.rest || training.travel) {
		return getRestTravelBarColorForHighcharts(training);
	}

	let top_color = "#d9534f";
	let bottom_color = "#31B452";

	if (wellness === true) {
		if (wellness_info["no_color"] === true) {
			return "#808080";
		}
		top_color = "#31B452";
		bottom_color = "#d9534f";

		if (wellness_info["low_good"] === true) {
			top_color = "#d9534f";
			bottom_color = "#31B452";
		}
		if (training[field] >= wellness_info["upper"]) {
			return top_color;
		} else if (training[field] >= wellness_info["lower"]) {
			return "#FFC83D";
		} else {
			return bottom_color;
		}
	}

	if (training.colors?.[field]) {
		return training.colors[field];
	}

	if (dataColorUtil) {
		return dataColorUtil.get_gps_color(field, training[field], 1, playerId)
	}

	return "#000000";
}

export const getMaxAndAverage = (data, metric, low_good) => {
	const metricData = data.map((item) => item[metric]);
	const mul = low_good ? -1 : 1;
	let maxValue = metricData[0];
	let sum = 0,
		count = 0;
	for (let item of metricData) {
		if (item === undefined) continue;
		if (maxValue === undefined || mul * item > mul * maxValue)
			maxValue = item;
		sum += item;
		count++;
	}
	if (count) {
		return [maxValue, sum / count];
	} else {
		return [];
	}
};

export const getBarWeightMetric = (metric, metricList) => {
	const temp = metric.substring(0, metric.lastIndexOf("(")) + "(Bar Weight)";
	if (metricList.includes(temp)) {
		return temp;
	} else {
		if (temp.startsWith("BB ")) {
			const withoutBB = temp.substring(3);
			if (metricList.includes(withoutBB)) {
				return withoutBB;
			} else {
				return null;
			}
		}
	}
};

export const getLowGoodData = (data) => {
	if (!data) return {};
	const lowGoodData = data?.reduce((cur, item) => {
		cur[[item.testing_metric_id__name]] = item.testing_metric_id__low_good;
		return cur;
	}, {});
	return lowGoodData;
};

export const FPChartLegend = (
	<>
		<div
			style={{
				border: "1px solid rgb(217, 217, 217)",
				padding: 8,
				display: "flex",
				flexDirection: "column",
				gap: 8,
				alignItems: "flex-start",
				width: "100%",
			}}
		>
			<div>
				<span
					style={{
						backgroundColor: "rgb(49, 180, 82)",
						padding: "3px",
					}}
				>
					Highest value in the period selected
				</span>
			</div>
			<div style={{ color: "rgb(49, 180, 82)" }}>
				One standard deviation above the mean
			</div>
			<div style={{ color: "black" }}>
				Within one standard deviation of the mean
			</div>
			<div style={{ color: "rgb(255, 200, 61)" }}>
				Between 1 and 2 standard deviations below the mean
			</div>
			<div style={{ color: "rgb(217, 83, 79)" }}>
				More than 2 standard deviations below the mean
			</div>
		</div>
	</>
);

export const getRestTravelLabel = (datapoint) => {
	if(datapoint.rest && datapoint.travel) {
		return "Rest + Travel";
	}

	if(datapoint.rest) {
		return "Rest";
	}

	if(datapoint.travel) {
		return "Travel"
	}

	return "";
}

export const downloadPDF = async (activeTab, setExportPreview) => {
	try {
		message.loading("Generating PDF...", 0);

		let tab = "";
		if (activeTab == "1") {
			tab = "tab-gpsdata";
		} else if (activeTab == "2") {
			tab = "tab-subjective";
		} else if (activeTab == "3") {
			tab = "tab-forceplate";
		} else if (activeTab == "4") {
			tab = "tab-testing";
		} else {
			tab = "";
		}
		const all_class_names = `exportable ${tab}`;
		console.log("---classnames", all_class_names);
		const elements = Array.from(
			document.getElementsByClassName(all_class_names)
		);
		console.log(elements);
		if (elements.length === 0) {
			message.destroy();
			message.info("Nothing to export");
			return;
		}

		const pdf = new jsPDF("p");
		const pdf_start_height = pdf.internal.pageSize.getHeight() * 0.05;
		let pdfHeight = pdf_start_height;

		const width = pdf.internal.pageSize.getWidth();
		let left = true;

		// HEADER
		pdf.setFontSize(10);
		pdf.setTextColor(40);
		pdf.text("GPS DataViz Report", width * 0.4, 5);

		for (let i = 0; i < elements.length; i++) {
			let element = elements[i];
			if (element.childElementCount == 0) continue;
			let width_scale = 0.4;
			let height_scale = 0.4;
			let is_table = false;

			if (element.className.includes("gpsdv-table")) {
				width_scale = 0.9;
				height_scale = 0.9;
				setExportPreview(true);
				await new Promise((r) => setTimeout(r, 500));
				//element = document.getElementsByClassName(`exportable gpsdv-table ${tab}`)[0]
				is_table = true;
				left = false;
			}

			if (element.className.includes("testing-stats")) {
				width_scale = 0.9;
				height_scale = 0.9;
				is_table = true;
				left = false;
			}

			if (element.className.includes("testing-stats-toggles")) {
				width_scale = 1.5;
				height_scale = 1.5;
				is_table = true;
				left = false;
			}

			if (element.className.includes("testing-radar")) {
				is_table = true;
				left = false;
			}

			if (element.className.includes("forceplate-eld-chart")) {
				width_scale = 1;
				height_scale = 1;
				is_table = true;
				left = false;
			}

			if (element.className.includes("breakdown-chart")) {
				width_scale = 0.9;
				height_scale = 0.9;
				is_table = true;
				left = false;
			}

			// if (element.className.includes("testing-radar")){
			//   width_scale = 1;
			//   height_scale = 1;
			//   is_table = true;
			//   left = false
			// }

			const canvas = await captureElement(element);
			const imgData = canvas.toDataURL("image/png");

			// if (element.className.includes("breakdown-table")){
			//   console.log(pdf.internal.pageSize.getHeight(), canvas.height);
			//   if (canvas.height > pdf.internal.pageSize.getHeight()){
			//     height_scale = (pdf.internal.pageSize.getHeight() / canvas.height) * 0.9
			//   }
			//   height_scale = (canvas.height / pdf.internal.pageSize.getHeight()) * 0.9
			// }

			setExportPreview(false);

			const height = (canvas.height * width) / canvas.width;
			let horizontal_mid = (width * (1 - width_scale)) / 2;

			if (
				pdfHeight + height * height_scale >
				pdf.internal.pageSize.getHeight()
			) {
				pdfHeight = pdf_start_height; // reset height for a new page
				if (i !== 0) {
					pdf.addPage();
					// HEADER
					pdf.setFontSize(10);
					pdf.setTextColor(40);
					pdf.text("GPS DataViz Report", width * 0.43, 5);
				}
			}

			let x = 0;
			let y = pdfHeight;
			if (is_table) {
				x = horizontal_mid;
			} else if (left) {
				x = width * 0.1;
			} else {
				x = width * (width_scale + 0.1);
			}
			if (left && i == elements.length - 1) {
				x = horizontal_mid;
			}

			pdf.addImage(
				imgData,
				"PNG",
				x,
				y,
				width * width_scale,
				Math.min(
					height * height_scale,
					pdf.internal.pageSize.getHeight()
				)
			);
			if (is_table || !left) {
				pdfHeight +=
					height * height_scale +
					pdf.internal.pageSize.getHeight() * 0.02;
			}
			left = !left;
		}
		message.destroy();
		message.success("PDF Exported!");
		pdf.save("download.pdf");
	} catch (err) {
		message.destroy();
		message.error(
			"Error generating PDF. Please refresh the page and try again."
		);
		console.error(err);
	}
};

export const HrColors = [
	"#9B9C9E",
	"#009FD7",
	"#009B4D",
	"#EDA500",
	"#D10E33",
	"#D10E33",
	"#D10E33",
	"#D10E33",
];

export const TRAFFIC_LIGHT_COLORS = [
	"rgb(49, 180, 82)", //green
	"rgb(255, 200, 61)", //yellow
	"rgb(217, 83, 79)", //red
];

export const distanceColors = {
	distance_1: "#90EE90",
	distance_2: "#00AB41",
	distance_3: "#FFD300",
	distance_4: "#FFA500",
	distance_5: "#DB0F27",
};

export const getStacks = (dataIn, keyPrefix) => {
	const stackMetrics =
		dataIn?.metrics?.filter(({ key }) => key.startsWith(keyPrefix)) || [];
	return stackMetrics.map(({ key, name }, ind) => ({
		key,
		label: name,
		color: HrColors[ind],
	}));
};

export const getChartType = (metric) => {
	if (!metric) return null;

	if (metric.metric_type === "forceplate") {
		const [app, metricName] = getAppName(metric.name);
		return (
			Object.values(asym_forceplate_metrics).some((metrics) =>
				metrics.includes(metricName)
			) && 
			!non_asym_forceplate_exercises.some(exercise_type =>
				metric?.name.startsWith(exercise_type)
			)
		)
			? "asym"
			: "regular";
	} else if (metric.metric_type === "testing") {
		if (metric?.metadata?.is_asym) {
			return "asym";
		}
		return "regular";
	} else if (metric.metric_type === "nutrition") {
		return "regular";
	} else if (metric.metric_type === "cognition") {
		return "bellcurve";
	} else {
		return null;
	}
};

export const getForceplateAsymGroup = (metricName) => {
	const [app, name] = getAppName(metricName);
	return Object.entries(asym_forceplate_metrics).find(([key, value]) =>
		value.includes(name)
	);
};

export const getTestingAsymGroup = (metricName) => {
	let nameWOPrefix = metricName;
	if (metricName.includes("-")) { 
		nameWOPrefix = metricName.split(" - ")[1];
	}
	
	let left, right, asym;
	if (nameWOPrefix.includes("Left")) {
		left = nameWOPrefix;
		right = nameWOPrefix.replace("Left", "Right");
		asym = nameWOPrefix.replace("Left", "Asym");
	} else if (nameWOPrefix.includes("Right")) {
		right = nameWOPrefix;
		left = nameWOPrefix.replace("Right", "Left");
		asym = nameWOPrefix.replace("Right", "Asym");
	} else if (nameWOPrefix.includes("Asym")) {
		asym = nameWOPrefix;
		right = nameWOPrefix.replace("Asym", "Right");
		left = nameWOPrefix.replace("Asym", "Left");
	}

	return [left, right, asym];
};

export const getAppName = (metricName) => {
	if (!metricName) {
		return [null, ""];
	}
	const [app, ...rest] = metricName.split(" - ");
	if (rest.length === 0) return [null, app];
	return [app, rest.join(" - ")];
};

export const groupByAppName = (metrics) => {
	const result = {};

	metrics.forEach((metric) => {
		const [app, metricName] = getAppName(metric.name);
		result[app] = [...(result[app] || []), metric];
	});

	return result;
};

export const getDropdownKey = (metric, keyType = "name") => {
	if (!metric) return null;
	if (keyType === "name") return metric.name;
	const id = metric.is_user_defined ? `c${metric.metric_id}` : metric.metric_id;
	return `${metric.metric_type}-${id}`;
};

export const generateMetricTreeWithApp = (
	metrics,
	metricType = "forceplate",
	keyType = "name"
) => {
	const appsData = groupByAppName(metrics);
	return Object.keys(appsData)
		?.reduce((acc, app) => {
			const subtree =
				metricType === "forceplate"
					? generateForceplateMetricTree(appsData[app], keyType)
					: generateTestingMetricTree(appsData[app], keyType);
			if (app === "null") {
				acc = acc.concat(subtree);
			} else {
				acc.push({
					key: app,
					label: app,
					children: subtree,
				});
			}
			return acc;
		}, [])
		?.sort((a, b) =>
			a.label?.toLowerCase() < b.label?.toLowerCase() ? -1 : 1
		);
};

export const generateNormalMetricTree = (metrics, keyType = "name") => {
	console.log("generateNormalMetricTree", metrics);
	try {
		let items = generateVBTMetricTree(metrics, keyType);
		let result = [];
		let generateTreeForGPSMetric = false;
		Object.keys(items).forEach((exercise, i) => {
			const children = items[exercise];
			let el = undefined;
			const metricData = metrics.find(
				({ name }) => getAppName(name)[1] === exercise
			);
			const dropdownKey = getDropdownKey(metricData, keyType);
			generateTreeForGPSMetric = dropdownKey?.startsWith("gps-");

			if (children.length > 0) {
				el = {
					key: dropdownKey,
					label: exercise,
					children: children,
				};
				result.push(el);
			} else {
				el = {
					key: dropdownKey,
					label: exercise,
					children: undefined,
				};
				result.push(el);
			}
		});
		
		if (generateTreeForGPSMetric) {
			return result;
		}
		else {
			result.sort((a, b) =>
				a.label?.toLowerCase() < b.label?.toLowerCase() ? -1 : 1
			);
		}
	} catch (err) {
		console.log("Error creating Testing metric tree", err);
		return [];
	}
};

export const generateForceplateMetricTree = (metrics, keyType = "name") => {
	try {
		let items = generateVBTMetricTree(metrics, keyType);
		let result = [];
		let visited = new Set();

		Object.keys(items).forEach((exercise, i) => {
			const children = items[exercise];
			let el = undefined;
			const metricData = metrics.find(
				({ name }) => getAppName(name)[1] === exercise
			);
			const dropdownKey = getDropdownKey(metricData, keyType);

			if (children.length > 0) {
				el = {
					key: dropdownKey,
					label: exercise,
					children: children,
				};
				result.push(el);
			} else {
				const chartType = getChartType(metricData);
				if (chartType === "asym") {
					const group = getForceplateAsymGroup(exercise);
					if (group && group.length && !visited.has(group[0])) {
						el = {
							key: dropdownKey,
							label: `[Asym] ${group[0]}`,
							children: undefined,
						};
						result.push(el);
						visited.add(group[0]);
					}
				} else {
					el = {
						key: dropdownKey,
						label: exercise,
						children: undefined,
					};
					result.push(el);
				}
			}
		});
		return result.sort((a, b) =>
			a.label?.toLowerCase() < b.label?.toLowerCase() ? -1 : 1
		);
	} catch (err) {
		console.log("Error creating Testing metric tree", err);
		return [];
	}
};

export const generateTestingMetricTree = (metrics, keyType = "name") => {
	try {
		let result = [];
		let visited = new Set();
		let items = generateVBTMetricTree(metrics, "name");

		const nordic_asym_metric_prefixes = [
			"Nordic Left",
			"Nordic Right",
			"Nordic Asym",
			"ISO 30",
			"ISO 60",
			"ISO Prone",
			"Razor",
			"NB Custom",
			"Asym"
		];

		Object.keys(items).forEach((exercise, i) => {
			const children = items[exercise];
			let el = undefined;
			const metricData = metrics.find(
				({ name }) => getAppName(name)[1] === exercise
			);
			const dropdownKey = getDropdownKey(metricData, keyType);

			for (let i = 0; i < nordic_asym_metric_prefixes.length; i++) {
				if (exercise.startsWith(nordic_asym_metric_prefixes[i])) {
					exercise = exercise
						?.replace("Left", "")
						?.replace("Right", "")
						?.replace("Asym", "")
						.trim();
					exercise = "[Asym] " + exercise;
				}
			}


			if (result.map((x) => x.label).includes(exercise) || (exercise.startsWith("Left") || exercise.startsWith("Right"))) {
				// do nothing, this already exists
			} else if (children.length > 0) {
				let keyed_children = children.map((child) => {
					let metricData = metrics.find((x) => getAppName(x.name)[1] == child.key);
					return {
						...child,
						key: getDropdownKey(metricData, "metricId"),
					};
				});
				el = {
					key: dropdownKey,
					label: exercise,
					children: keyed_children,
				};
				result.push(el);
			} else {
				const charType = getChartType(metricData);
				if (charType === "asym") {
					const [app, metricName] = getAppName(metricData.name);
					const groupName = metricName
						?.replace("Left", "")
						?.replace("Right", "");
					if (groupName && !visited.has(groupName)) {
						el = {
							key: dropdownKey,
							label: `[Asym] ${groupName}`,
							children: undefined,
						};
						result.push(el);
						visited.add(groupName);
					}
				} else {
					el = {
						key: dropdownKey,
						label: exercise,
						children: undefined,
					};
					result.push(el);
				}
			}
		});
		return result.sort((a, b) =>
			a.label?.toLowerCase() < b.label?.toLowerCase() ? -1 : 1
		);
	} catch (err) {
		console.log("Error creating Testing metric tree", err);
		return [];
	}
};

export const getPercentageValues = (rawValue = 0, decimals, rawValues = [], low_good = false) => {

	if(rawValue === 0) {
		return 0;
	}

	if(low_good) {
		const filteredRawValues = rawValues.filter( (value) => value)
		if(rawValue < 0) {
			return  Number(100 * (1 / (rawValue / Math.min(...filteredRawValues))).toFixed(decimals))
		  } else {
			return Number(100 * (1 / (rawValue / Math.min(...filteredRawValues))).toFixed(decimals))
		  }
	}

	// low_good is false
    if(rawValue < 0) {
      return  Number(100 * rawValue / Math.min(...rawValues).toFixed(decimals))
    } else {
      return Number(100 * rawValue / Math.max(...rawValues).toFixed(decimals))
    }
}

export const getMetricDropdownLabel = (metric, isCompact) => {
	if (!metric) return "Choose Metric";
	const [app, metricName] = getAppName(metric.name);
	const prefix = app !== null ? app + " - " : "";
	if (getChartType(metric) === "asym") {
		if (metric.metric_type === "forceplate") {
			const exerciseName = getForceplateAsymGroup(metric?.name)[0];
			if (isCompact)
				return (
					<React.Fragment>
						{app} [Asym]
						{exerciseName && (
							<>
								<br />
								{exerciseName}
							</>
						)}
					</React.Fragment>
				);
			else return prefix + "[Asym] " + exerciseName;
		} else {
			return (
				prefix +
				"[Asym] " +
				metricName
					?.replace("Left", "")
					?.replace("Right", "")
					?.replace("Asym", "")
					.trim()
			);
		}
	} else {
		if (isCompact)
			return (
				<React.Fragment>
					{app}
					{metricName && (
						<>
							<br />
							{metricName}
						</>
					)}
				</React.Fragment>
			);
		else return prefix + metricName;
	}
};

export const getMetricDropdownList = (all_metric_types, metricType) => {

	const gpsSubmenu = generateNormalMetricTree(
		all_metric_types["gps"] || [],
		"metricId"
	);
	const wellnessSubmenu = generateNormalMetricTree(
		all_metric_types["wellness"] || [],
		"metricId"
	);

	const menuItems =
		metricType === "gps"
			? gpsSubmenu
			: metricType === "wellness"
			? wellnessSubmenu
			: metricType === "forceplate"
			? []
			: metricType === "testing"
			? []
			: [
					{ label: "GPS", key: "gps", children: gpsSubmenu },
					{
						label: "Wellness",
						key: "wellness",
						children: wellnessSubmenu,
					},
			  ].filter((menu) => menu?.children?.length > 0);

	return menuItems;
};

/**
 * Determines if labels should be rotated for subjective/wellness metric charts
 * @param {Array} series - The series data array
 * @returns {boolean} - True if labels should be rotated
 */
export const shouldRotate = (series) => {
  if (!series || !Array.isArray(series)) {
    return false;
  }

  // Rotate if series length is > 4
  if (series.length > 4) {
    return true;
  }

  // Rotate if any label is longer than 5 characters
  return series.some(item => {
    const label = item?.text || item?.name || '';
    return label.length > 5;
  });
};

export const getXAxisLabelRotation = (chart, labelVals) => {
    chart.update({
      xAxis: {
        labels: (
          labelVals.length > 20
          ? 
          {
            rotation: -90,
            overflow: 'justify',
            staggerLines: 1,
            step: 0
          }
          : 
          (
            labelVals.length > 10
            ? 
            {
              rotation: -45,
              overflow: 'justify',
              staggerLines: 1,
              step: 0
            }
            : 
            {
              rotation: 0,
            }
          )
        ),
      }
    });
        
  }

export	const getMappingFunction = ({
	dataIn, 
	metric, 
	chartType = null, 
	showTitle = true, 
	showACWR=false, 
	showTravel=false, 
	showRest=false,
	restAndTravel,
	startDate,
	endDate,
	teamName,
	showPredictions,
	games,
	teamColors,
	acwrThresholdConfig,
	customChartData,
	dataColorUtil
}
) => {
		let customChart = chartType ? metric : null
		if(metric?.customChartData) {
			customChart = metric?.customChartData
			chartType = metric?.customChartData?.type
		}

		// check if the team has custom chart available for this metric
		const customCharts = customChartData?.chart_types || []
		const chart = customCharts.find( (chart) => metric?.key === chart?.bar1_metric || metric?.key === chart?.bar_metric || metric?.key === chart?.stack_type)
		if(chart) {
			customChart = chart
			chartType = chart.type
		}
	
		switch (chartType) {
			case 'bar_and_bubble_chart': {
				return mapDataForBarAndBubbleChart({
					dataIn,
					mainSeriesMetric: dataIn.metrics.find( (metric) => metric.key === customChart?.bar_metric),
					secondarySeriesMetric: dataIn.metrics.find( (metric) => metric.key === customChart?.bubble_metric),
					showACWR,
					showRest,
					showTravel,
					restAndTravel,
					startDate,
					endDate,
					teamName,
					showPredictions,
					games,
					teamColors: teamColors.all,
					showAvgPlotline: true,
					acwrThresholdConfig,
					showTitle,
					customTitle: customChart?.title,
					dataColorUtil
				});
			}
			case 'grouped_bar_chart': {
				return mapDataForHighchartsGroupedBar({
					dataIn,
					games,
					metrics: dataIn.metrics.filter( (metric) => metric.key === customChart?.bar1_metric || metric.key === customChart?.bar2_metric),
					teamColors: teamColors.all,
					isComparison: false,
					showACWR,
					showRest,
					showTravel,
					restAndTravel,
					startDate,
					endDate,
					showPredictions,
					isTeamChart: false,
					isMetricComparisonChart: true,
					title: customChart.title,
					showTitle
				});
			}
			case 'stacked_bar_chart': {
				return stackedBarChartDtoToModel({
					data: dataIn?.data,
					showRest,
					showTravel,
					restAndTravel,
					startDate,
					endDate,
					chartType: customChart?.stack_type,
					showPredictions,
					colors_100: dataIn?.colors,
					games,
					teamName,
					showACWR: customChart?.stack_type === 'distance' ? false : true,
					customChartConfig: customChart,
					metrics: dataIn?.metrics,
					acwrThresholdConfig,
					showTitle
				});
			}
			default: {
				// custom chart type either not provided or not recognized.
				// Will get handled by the code below
			}
		}
	
		if(MAX_VELO_METRICS.concat(TOTAL_DISTANCE_METRICS).includes(metric?.key)) {
		  	return mapDataForMaxVeloChart({
				dataIn,
				metric,
				showRest,
				showTravel,
				restAndTravel,
				startDate,
				endDate,
				showACWR,
				games,
				teamName: teamName,
				showPredictions,
				teamColors: teamColors?.all,
				acwrThresholdConfig,
				showTitle,
				dataColorUtil
		  	})
		}
		 
		return mapDataForHighcharts({
			dataIn,
			metric,
			showACWR,
			showRest,
			showTravel,
			restAndTravel,
			startDate,
			endDate,
			games,
			teamName: teamName,
			showPredictions,
			teamColors: teamColors?.all,
			teamMetric,
			acwrThresholdConfig,
			showTitle,
			dataColorUtil
		});
}

export const getMetricInfoByIdentifier = (metricIdWithStat, all_metric_types) => {
	const {metric_id, metric_stat} = get_metric_info_from_identifier(metricIdWithStat);
	
	const metric = Object.values(all_metric_types)
				.flat()
				.find((metric) => getDropdownKey(metric, "metricId") === metric_id);

	const title = !metric ? "Choose Metric" : getMetricDropdownLabel(metric);
	return [title, metric];
}